<template>
    <!--
        Month chooser for DateRangePicker: a year header and one column of
        months. Emits `select` with the whole month as { start, end } luxon
        DateTimes.
    -->
    <vue-datepicker class="drp-month-pane"
                    inline
                    month-picker
                    auto-apply
                    :config="{ modeHeight: 294 }"
                    :max-date="maxDate ? maxDate.toJSDate() : null"
                    prevent-min-max-navigation
                    month-name-format="long"
                    :model-value="pickerValue"
                    @update:model-value="onUpdate"
    ></vue-datepicker>
</template>

<script>
import VueDatepicker from '@vuepic/vue-datepicker'
import { monthRange } from '../../../utils/dateRangePresets'

export default {
    name: 'DateRangeMonthPane',
    components: { VueDatepicker },
    emits: ['select'],
    props: {
        // luxon DateTime; its month is the one shown as selected.
        start: {
            type: Object,
            default: null,
        },
        // Last selectable day, or null for no limit.
        maxDate: {
            type: Object,
            default: null,
        },
    },
    computed: {
        pickerValue() {
            if (!this.start) {
                return null
            }
            return { month: this.start.month - 1, year: this.start.year }
        },
    },
    methods: {
        onUpdate(value) {
            if (!value || (value.year === undefined)) {
                return
            }
            this.$emit('select', monthRange(value.year, value.month + 1))
        },
    },
}
</script>

<style scoped>
/* The picker sizes the menu and the month overlay to --dp-menu-min-width
   (260px); stretch them so the month list fills the pane. The theme class sits
   on both the root and the menu, so set the variable on each. */
.drp-month-pane.dp__theme_light,
.drp-month-pane :deep(.dp__theme_light) {
    --dp-menu-min-width: 100%;
}

.drp-month-pane > :deep(div:has(.dp__outer_menu_wrap)) {
    flex: 1;
}

/* The year header, then one scrolling column of months instead of the 3x4 grid. */
.drp-month-pane :deep(.dp__overlay_container) {
    scrollbar-width: none;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    height: 100%;
}

.drp-month-pane :deep(.dp__overlay_container::-webkit-scrollbar) {
    display: none;
}

.drp-month-pane :deep(.dp__selection_grid_header) {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #fff;
    padding-bottom: 0.4rem;
}

.drp-month-pane :deep(.dp--year-mode-picker) {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.drp-month-pane :deep(.dp--year-select) {
    order: -1;
    flex: 1;
    justify-content: flex-start;
    padding: 0;
    font-weight: 600;
    color: #111827;
}

.drp-month-pane :deep(.dp--arrow-btn-nav) {
    height: 2.4rem;
    width: 2.4rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
}

.drp-month-pane :deep(.dp__overlay_row) {
    display: contents;
}

.drp-month-pane :deep(.dp__overlay_col) {
    width: 100%;
    padding: 0;
}

.drp-month-pane :deep(.dp__overlay_cell),
.drp-month-pane :deep(.dp__overlay_cell_active) {
    height: auto;
    border-radius: 0.375rem;
    background: #f3f4f6;
    color: #374151;
    text-align: left;
    padding: 0.6rem 0.75rem;
}

.drp-month-pane :deep(.dp__overlay_cell_active) {
    background: var(--dp-primary-color);
    color: #fff;
}

.drp-month-pane :deep(.dp__overlay_cell_disabled) {
    cursor: not-allowed;
    opacity: 0.4;
}
</style>
